import { Project } from '../models/project.model';
import { BLOCK_GP_MASTER, GP_NAME_ALIASES } from '../constants/field-execution-master-data';
import { FIELD_EXECUTION_BLOCKS } from '../services/field-execution.service';

const FE_BLOCK_KEYS = new Set(
    FIELD_EXECUTION_BLOCKS.map(b => b.toLowerCase().replace(/\s+/g, ' ').trim())
);
FE_BLOCK_KEYS.add('dhamnagar');

const ALL_FE_GP_KEYS = new Set<string>();
for (const gps of Object.values(BLOCK_GP_MASTER)) {
    for (const gp of gps) {
        ALL_FE_GP_KEYS.add(gp.toLowerCase().replace(/\s+/g, ' ').trim());
    }
}
for (const [alias, canon] of Object.entries(GP_NAME_ALIASES)) {
    ALL_FE_GP_KEYS.add(alias.toLowerCase().replace(/\s+/g, ' ').trim());
    ALL_FE_GP_KEYS.add(canon.toLowerCase().replace(/\s+/g, ' ').trim());
}

function normalizeGpLabel(value: string): string {
    return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

/** True when village / loc matches a known Field Execution GP name. */
export function isFieldExecutionGpLocation(p: Project): boolean {
    const raw = (p.village || p.loc || '').trim();
    if (!raw) return false;

    const key = normalizeGpLabel(raw);
    const compact = key.replace(/\s/g, '');
    const aliased = GP_NAME_ALIASES[compact] ?? GP_NAME_ALIASES[key];
    const canon = aliased ?? raw;

    if (ALL_FE_GP_KEYS.has(normalizeGpLabel(canon))) return true;
    if (ALL_FE_GP_KEYS.has(key)) return true;
    if (ALL_FE_GP_KEYS.has(compact)) return true;
    return false;
}

/** True when a document belongs to Field Execution (block import / GP sheets). */
export function isFieldExecutionProject(p: Project): boolean {
    const scope = (p as Project & { projectScope?: string }).projectScope;
    if (scope === 'field_execution') return true;
    if (scope === 'hm') return false;

    const block = (p.block ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (block && FE_BLOCK_KEYS.has(block)) return true;

    if (isFieldExecutionGpLocation(p)) return true;

    return false;
}

/** True when a document is explicitly HM-tagged (priority, visibility, or scope). */
export function isHmTaggedProject(p: Project): boolean {
    const scope = (p as Project & { projectScope?: string }).projectScope;
    if (scope === 'hm') return true;
    return !!(
        p.hmPriority ||
        p.recurringIntervention ||
        p.visibility === 'HM Committed' ||
        p.visibility === 'High Visibility' ||
        p.priority
    );
}

/**
 * Lightweight HM check for non-injected code paths.
 * Prefer {@link FirestoreProjectService.isValidHmProject} (resolves master departments).
 */
export function isHmDecisionProject(p: Project): boolean {
    if (isFieldExecutionProject(p)) return false;
    if (isHmTaggedProject(p)) return true;
    return !!(p.dept ?? '').trim();
}
